import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { quotesService } from '../../services/quotesService';
import { clientsService } from '../../services/clientsService';
import { uploadService } from '../../services/uploadService';
import { itinerariesService } from '../../services/itinerariesService';
import { UserSearch, UserPlus, Calendar, Users, Plus, Check, ChevronRight } from 'lucide-react';
import { useToast } from '../common/Toast';
import Modal from '../common/Modal';
import './QuoteBuilder.css';
import './components/ComponentForms.css';

// Component type registry (categories/types, form-per-type switch, and the
// componentData->saved-component builder) - shared with EditItineraryModal.jsx
// so quotes and itineraries edit components identically. See
// componentFormRegistry.js for why this is centralized here.
import {
  componentCategories,
  componentTypes,
  inferComponentType,
  getComponentFormInitialData,
  renderComponentTypeForm,
  buildSavedComponent
} from './componentFormRegistry';
import ClientModal from './ClientModal';
import ComponentLibraryModal from './Library/ComponentLibraryModal';
import PDFQuoteUploadModal from './PDFQuoteUploadModal';
import PDFQuoteReviewModal from './PDFQuoteReviewModal';
import ConversationalQuoteBuilder from './ConversationalQuoteBuilder';
import QuoteRenderer from '../templates/QuoteRenderer';
import QuoteOptionTabs from './QuoteOptionTabs';

const QuoteBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { id: urlId } = useParams(); // Get quote ID from URL for editing
  const [quoteId, setQuoteId] = useState(urlId); // State-based quote ID
  const isEditMode = !!quoteId; // Reactive edit mode based on state
  // Whether this page load was a genuine "open an existing quote to edit"
  // (or a lead-conversion landing, which also arrives with a real :id in
  // the URL) vs. a brand-new quote (route "quotes/new", no urlId) that
  // later acquires a quoteId mid-session via autosave/save-draft. Captured
  // once at mount - quoteId itself changes over time (autosave/handleSave
  // assign it as soon as the draft is first persisted), which would
  // otherwise make fetchQuote()'s one-time "which step should this land
  // on" decision re-run and yank the agent to Review & Send the moment
  // the very first autosave fires after just picking a client, before
  // they've touched Trip Details/Components at all.
  const isEditModeFromUrlRef = useRef(!!urlId);

  // Quote state
  const [quote, setQuote] = useState({
    quoteTitle: '',
    client: null,
    travelDates: {
      start: '',
      end: ''
    },
    travelers: {
      adults: 1,
      children: []
    },
    components: [],
    options: [], // For package comparisons
    totalPrice: 0,
    depositAmount: 0,
    depositDueDate: '',
    notes: '',
    status: 'draft',
    agentInfo: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      agencyName: user?.agencyName || ''
    }
  });

  // UI state
  const [activeStep, setActiveStep] = useState('client'); // client, details, components, preview
  const [showClientModal, setShowClientModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showPDFUploadModal, setShowPDFUploadModal] = useState(false);
  const [showPDFReviewModal, setShowPDFReviewModal] = useState(false);
  const [showConversationalBuilder, setShowConversationalBuilder] = useState(false);
  const [pdfExtractedData, setPdfExtractedData] = useState(null);
  const [selectedComponentType, setSelectedComponentType] = useState('');
  const [editingComponent, setEditingComponent] = useState(null);
  const [depositManuallyOverridden, setDepositManuallyOverridden] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('modern'); // Template/Theme selection

  // Multi-option AI quote comparison state
  // quoteOptionGroup holds one entry per sibling option: { id, optionName, data }
  // `data` is the full builder-shaped quote object for that option, kept in sync
  // whenever the agent switches tabs so edits aren't lost.
  const [quoteOptionGroup, setQuoteOptionGroup] = useState([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [showSendModal, setShowSendModal] = useState(false);

  // Autosave: 'idle' (nothing to save yet) | 'unsaved' (edits pending,
  // debounce running) | 'saving' | 'saved' | 'error'. Scoped to the single-
  // quote case only (not the multi-option AI comparison group) - that flow
  // already has its own more involved parallel-save logic in
  // handleSaveQuote and is out of scope here.
  const [saveStatus, setSaveStatus] = useState('idle');
  const autosaveTimerRef = useRef(null);
  // Guards against autosaving immediately after a fresh load/hydration
  // (fetchQuote, AI quote generation) mistaking that for a user edit.
  const skipNextAutosaveRef = useRef(true);
  // Gates the very first autosave on a brand-new quote until the agent has
  // actually completed Trip Details (title + dates) and clicked through to
  // Components - not just picked a client. Picking a client alone used to
  // be enough to trigger a save, which persisted a near-empty draft and is
  // what surfaced the title-coalesced-to-null bug (see commit 73eb81b).
  // Once true it stays true for the rest of the session - this only exists
  // to delay the *first* save, not to re-gate every future edit. Any path
  // that hydrates a quote which already exists in the DB (opening an
  // existing quote, a lead conversion, an AI-generated quote) sets this
  // immediately, since Trip Details necessarily already happened for that
  // record.
  const detailsStepCompletedRef = useRef(!!urlId);

  // Load clients on mount
  useEffect(() => {
    fetchClients();
    if (isEditMode) {
      fetchQuote();
    }
  }, [quoteId, isEditMode]);

  const fetchClients = async () => {
    try {
      const clients = await clientsService.getAll();
      setClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setErrors({ fetch: 'Failed to load clients' });
    }
  };

  const fetchQuote = async () => {
    try {
      setLoadingQuote(true);
      console.log('Fetching quote with ID:', quoteId); // Debug log
      const quoteData = await quotesService.getById(quoteId);
      
      console.log('Loaded quote data:', quoteData); // Debug log
      
      // Check if quote has been converted to an itinerary
      if (quoteData.status === 'converted') {
        console.log('Quote has been converted, finding corresponding itinerary...');
        try {
          const itinerary = await itinerariesService.findByQuoteId(quoteId);
          console.log('Found itinerary:', itinerary);
          toast.info('This quote has been converted to an itinerary. Redirecting you to the itinerary...');
          navigate(`/agent/itineraries`);
          return;
        } catch (itineraryError) {
          console.error('Error finding itinerary:', itineraryError);
          toast.warning('This quote has been converted to an itinerary, but we could not find the itinerary. Redirecting to itineraries list...');
          navigate('/agent/itineraries');
          return;
        }
      }
      
      // Parse JSON strings if they come as strings
      let componentsArray = [];
      let optionsArray = [];
      
      console.log('Raw components from DB:', quoteData.components);
      console.log('Components type:', typeof quoteData.components);
      
      try {
        // Handle components - check for null, string, or array
        if (quoteData.components === null || quoteData.components === undefined) {
          componentsArray = [];
          console.log('Components is null/undefined, using empty array');
        } else if (typeof quoteData.components === 'string') {
          componentsArray = JSON.parse(quoteData.components) || [];
          console.log('Parsed components array:', componentsArray);
          console.log('Components count after parsing:', componentsArray.length);
        } else if (Array.isArray(quoteData.components)) {
          componentsArray = quoteData.components;
          console.log('Components already array:', componentsArray.length);
        } else {
          console.warn('Unexpected components type:', typeof quoteData.components, quoteData.components);
          componentsArray = [];
        }
        
        // Handle options - check for null, string, or array
        if (quoteData.options === null || quoteData.options === undefined) {
          optionsArray = [];
        } else if (typeof quoteData.options === 'string') {
          optionsArray = JSON.parse(quoteData.options) || [];
        } else if (Array.isArray(quoteData.options)) {
          optionsArray = quoteData.options;
        } else {
          optionsArray = [];
        }
      } catch (parseError) {
        console.error('Error parsing JSON data:', parseError);
        console.error('Failed to parse components:', quoteData.components);
        // Set defaults if parsing fails
        componentsArray = [];
        optionsArray = [];
      }
      
      // Transform the quote data from API to component format
      const transformedQuote = {
        id: quoteData.id || quoteId, // Include the quote ID for preview functionality
        quoteTitle: quoteData.title || '',
        client: {
          id: quoteData.client_id,
          firstName: quoteData.client_name?.split(' ')[0] || '',
          lastName: quoteData.client_name?.split(' ').slice(1).join(' ') || '',
          email: quoteData.client_email || '',
          phone: quoteData.client_phone || ''
        },
        travelDates: {
          start: quoteData.travel_start_date ? quoteData.travel_start_date.split('T')[0] : '',
          end: quoteData.travel_end_date ? quoteData.travel_end_date.split('T')[0] : ''
        },
        travelers: {
          adults: quoteData.adults || 1,
          children: Array(quoteData.children || 0).fill({ age: 0 })
        },
        components: componentsArray,
        options: optionsArray,
        totalPrice: parseFloat(quoteData.total_amount) || 0,
        depositAmount: parseFloat(quoteData.deposit_amount) || 0,
        depositDueDate: quoteData.deposit_due_date ? quoteData.deposit_due_date.split('T')[0] : '',
        notes: quoteData.notes || '',
        status: quoteData.status || 'draft',
        agentInfo: {
          name: user?.name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          agencyName: user?.agencyName || '',
          termsConditions: quoteData?.terms_conditions || ''
        }
      };
      
      console.log('Final transformed quote components:', transformedQuote.components);
      console.log('Components in transformed quote:', transformedQuote.components.length);
      
      skipNextAutosaveRef.current = true;
      // This quote already exists in the DB, so Trip Details already
      // happened for it at some point - future edits should autosave
      // normally regardless of which step the agent lands on below.
      detailsStepCompletedRef.current = true;
      setQuote(transformedQuote);
      
      // Check if deposit was manually overridden (differs from auto-calculated)
      const loadedDeposit = parseFloat(quoteData.deposit_amount) || 0;
      const calculatedDeposit = calculateTotalDeposit(componentsArray);
      if (Math.abs(loadedDeposit - calculatedDeposit) > 0.01) {
        console.log('🔒 Detected manual deposit override:', { loaded: loadedDeposit, calculated: calculatedDeposit });
        setDepositManuallyOverridden(true);
      }
      
      // Set the selected template from the database
      if (quoteData.template_id) {
        setSelectedTemplate(quoteData.template_id);
        console.log('Loaded template:', quoteData.template_id);
      }
      
      // If we have data, show the preview step instead of client step. Only
      // do this on a genuine "open an existing quote" load (real :id in the
      // URL from the start, e.g. clicking a quote in QuotesList, or a lead
      // conversion landing) - NOT when this fetchQuote() call was triggered
      // by quoteId being assigned mid-session (autosave or Save Draft on a
      // brand-new "quotes/new" quote), which would otherwise yank the agent
      // straight to Review & Send the moment the client is selected and the
      // very first autosave fires, before Trip Details/Components are ever
      // shown.
      if (isEditModeFromUrlRef.current && transformedQuote.client && transformedQuote.client.id) {
        if (location.state?.fromLeadConversion) {
          // Coming straight from "Convert to Quote" on a lead: land on the
          // Components step instead of jumping all the way to Review & Send
          // so the agent actually sees what was pre-filled (client, dates,
          // and the auto-built component) before deciding it's ready to
          // send, rather than skipping 3 of the 4 steps silently.
          setActiveStep('components');

          const summaryParts = [];
          const clientName = [transformedQuote.client.firstName, transformedQuote.client.lastName]
            .filter(Boolean)
            .join(' ');
          if (clientName) summaryParts.push(`client ${clientName}`);
          if (transformedQuote.travelDates.start) summaryParts.push('travel dates');
          const partySize = transformedQuote.travelers.adults + transformedQuote.travelers.children.length;
          summaryParts.push(`${partySize} traveler${partySize === 1 ? '' : 's'}`);
          const firstComponent = componentsArray[0];
          if (firstComponent) {
            const typeLabel = componentTypes.find((t) => t.id === firstComponent.type)?.name || firstComponent.type;
            summaryParts.push(`a ${typeLabel} component`);
          }

          toast.info(
            `Quote created from lead — ${summaryParts.join(', ')} were pre-filled from the conversation. Review the details below before sending.`,
            { duration: 8000 }
          );
        } else {
          setActiveStep('preview');
        }
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setErrors({ fetch: 'Failed to load quote' });
      toast.error('Failed to load quote. Please try again.');
      navigate('/agent/quotes');
    } finally {
      setLoadingQuote(false);
    }
  };

  // Calculate total price when components change
  useEffect(() => {
    try {
      // Make sure components and options are arrays
      const components = Array.isArray(quote.components) ? quote.components : [];
      const options = Array.isArray(quote.options) ? quote.options : [];
      
      // Calculate total from components using new function that respects bundled components
      const total = calculateTotalPrice(components);
      
      // Calculate total from options
      const optionsTotal = options.reduce((sum, option) => {
        // Make sure option.components is an array
        const optComponents = Array.isArray(option.components) ? option.components : [];
        
        const optionSum = optComponents.reduce((optSum, comp) => {
          const price = parseFloat(comp.price) || 0;
          return optSum + price;
        }, 0);
        
        return sum + optionSum;
      }, 0);
      
      // Calculate total deposit from components
      const totalDeposit = calculateTotalDeposit(components);
      console.log('💰 useEffect updating quote - totalPrice:', total + optionsTotal, 'depositAmount:', totalDeposit, 'manualOverride:', depositManuallyOverridden);
      
      // Update quote with calculated total and deposit (only update deposit if not manually overridden)
      setQuote(prev => ({ 
        ...prev, 
        totalPrice: total + optionsTotal,
        depositAmount: depositManuallyOverridden ? prev.depositAmount : totalDeposit 
      }));
    } catch (error) {
      console.error('Error calculating total price:', error);
      // Keep existing total price if calculation fails
    }
  }, [quote.components, quote.options, depositManuallyOverridden]);

  // Handle client selection
  const handleClientSelect = (client) => {
    // Normalize client data structure
    const normalizedClient = {
      ...client,
      firstName: client.firstName || client.first_name,
      lastName: client.lastName || client.last_name
    };
    setQuote(prev => ({ ...prev, client: normalizedClient }));
    setShowClientModal(false);
    setActiveStep('details');
  };

  // Handle new client creation
  const handleNewClient = async (clientData) => {
    try {
      setLoading(true);
      const response = await clientsService.create(clientData);
      
      // Backend now returns full client object instead of just {clientId, message}
      // Normalize the client data to ensure consistent property names
      const newClient = {
        id: response.id || response.clientId,
        firstName: response.first_name || response.firstName,
        lastName: response.last_name || response.lastName,
        email: response.email,
        phone: response.phone,
        first_name: response.first_name,
        last_name: response.last_name,
        ...response
      };
      
      setClients(prev => [...prev, newClient]);
      handleClientSelect(newClient);
    } catch (error) {
      console.error('Error creating client:', error);
      setErrors({ client: 'Failed to create client' });
    } finally {
      setLoading(false);
    }
  };

  // Transform a raw database quote record (from the AI Quote Builder finalize
  // endpoint) into the shape used by the builder's `quote` state.
  const transformAIQuoteToBuilderShape = (finalizedQuoteData, isMultiOption, optionName) => {
    // Parse JSON strings if they come as strings (from database)
    let componentsArray = [];
    let optionsArray = [];

    try {
      if (finalizedQuoteData.components === null || finalizedQuoteData.components === undefined) {
        componentsArray = [];
      } else if (typeof finalizedQuoteData.components === 'string') {
        componentsArray = JSON.parse(finalizedQuoteData.components) || [];
      } else if (Array.isArray(finalizedQuoteData.components)) {
        componentsArray = finalizedQuoteData.components;
      }

      if (finalizedQuoteData.options === null || finalizedQuoteData.options === undefined) {
        optionsArray = [];
      } else if (typeof finalizedQuoteData.options === 'string') {
        optionsArray = JSON.parse(finalizedQuoteData.options) || [];
      } else if (Array.isArray(finalizedQuoteData.options)) {
        optionsArray = finalizedQuoteData.options;
      }
    } catch (parseError) {
      console.error('Error parsing AI quote components:', parseError);
      componentsArray = [];
      optionsArray = [];
    }

    const baseTitle = finalizedQuoteData.title || finalizedQuoteData.trip_name || '';
    const displayTitle = isMultiOption && optionName ? `${baseTitle} - ${optionName}` : baseTitle;

    return {
      id: finalizedQuoteData.id,
      quoteTitle: displayTitle,
      client: {
        id: finalizedQuoteData.client_id,
        firstName: finalizedQuoteData.client_first_name || '',
        lastName: finalizedQuoteData.client_last_name || '',
        email: finalizedQuoteData.client_email || '',
        phone: finalizedQuoteData.client_phone || ''
      },
      travelDates: {
        start: finalizedQuoteData.travel_start_date ? finalizedQuoteData.travel_start_date.split('T')[0] :
               finalizedQuoteData.start_date ? finalizedQuoteData.start_date.split('T')[0] : '',
        end: finalizedQuoteData.travel_end_date ? finalizedQuoteData.travel_end_date.split('T')[0] :
             finalizedQuoteData.end_date ? finalizedQuoteData.end_date.split('T')[0] : ''
      },
      travelers: {
        adults: finalizedQuoteData.adults || 1,
        children: Array(finalizedQuoteData.children || 0).fill({ age: 0 })
      },
      components: componentsArray,
      options: optionsArray,
      totalPrice: parseFloat(finalizedQuoteData.total_amount || finalizedQuoteData.total_price || 0),
      depositAmount: parseFloat(finalizedQuoteData.deposit_amount || 0),
      depositDueDate: finalizedQuoteData.deposit_due_date ? finalizedQuoteData.deposit_due_date.split('T')[0] : '',
      notes: finalizedQuoteData.notes || '',
      status: finalizedQuoteData.status || 'draft',
      agentInfo: {
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        agencyName: user?.agencyName || '',
        termsConditions: finalizedQuoteData?.terms_conditions || ''
      }
    };
  };

  // Handle AI Quote Builder finalization
  // `finalizedQuoteData` is either a single database quote record, or (for
  // multi-option "Good/Better/Best" quotes) an array of sibling quote records
  // that share a parent_quote_id.
  const handleAIQuoteFinalized = (finalizedQuoteData) => {
    console.log('AI Quote finalized, hydrating quote builder:', finalizedQuoteData);

    if (Array.isArray(finalizedQuoteData)) {
      // Multi-option quote: build the comparison group and load the first option
      const group = finalizedQuoteData.map((rawQuote) => {
        const optionName = rawQuote.option_name || null;
        const transformed = transformAIQuoteToBuilderShape(rawQuote, true, optionName);
        return {
          id: rawQuote.id,
          optionName: optionName || 'Option',
          data: transformed
        };
      });

      setQuoteOptionGroup(group);
      setActiveOptionIndex(0);
      setQuoteId(group[0].id);
      setQuote(group[0].data);

      console.log(`Quote builder hydrated with ${group.length} multi-option AI quotes:`, group.map(g => g.optionName));
    } else {
      // Single quote (existing behavior)
      setQuoteOptionGroup([]);
      setActiveOptionIndex(0);

      const transformedQuote = transformAIQuoteToBuilderShape(finalizedQuoteData, false, null);
      setQuoteId(finalizedQuoteData.id);
      skipNextAutosaveRef.current = true;
      // The AI Quote Builder already created a real, persisted quote record
      // with Trip Details filled in - further edits here should autosave
      // normally.
      detailsStepCompletedRef.current = true;
      setQuote(transformedQuote);

      console.log('Quote builder hydrated with single AI-generated quote:', transformedQuote);
    }

    // Close the conversational builder modal
    setShowConversationalBuilder(false);

    // Transition to preview step to show the populated quote
    setActiveStep('preview');
  };

  // Switch the active tab in a multi-option quote comparison.
  // Persists any in-progress edits on the currently active option into
  // quoteOptionGroup before loading the target option's data into `quote`,
  // so nothing the agent typed/changed is lost when going back and forth.
  const handleSwitchOption = (newIndex) => {
    if (newIndex === activeOptionIndex) return;

    setQuoteOptionGroup(prevGroup => {
      const updatedGroup = prevGroup.map((option, idx) =>
        idx === activeOptionIndex ? { ...option, data: quote } : option
      );

      const target = updatedGroup[newIndex];
      if (target) {
        setQuote(target.data);
        setQuoteId(target.id);
      }

      return updatedGroup;
    });

    setActiveOptionIndex(newIndex);
  };

  // Calculate total price from all components (excluding bundled components)
  const calculateTotalPrice = (components) => {
    console.log('🔍 calculateTotalPrice called with components:', components);
    const result = components.reduce((total, component) => {
      const isOptional = component.isOption === true || component.data?.isOption === true;
      const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
      
      // For optional components with bundled timing, don't add to total price
      if (isOptional && paymentTiming === 'bundled') {
        console.log(`Component ${component.title}: bundled, not adding to total price`);
        return total;
      }
      
      // For all other components, add the price
      const price = parseFloat(component.price) || 0;
      console.log(`Component ${component.title}: adding $${price} to total price`);
      return total + price;
    }, 0);
    console.log('🔍 calculateTotalPrice result:', result);
    return result;
  };

  // Calculate total deposit from all components
  const calculateTotalDeposit = (components) => {
    console.log('🔍 calculateTotalDeposit called with components:', components);
    const result = components.reduce((total, component) => {
      let depositAmount = 0;
      const isOptional = component.isOption === true || component.data?.isOption === true;
      
      // For optional components, check paymentTiming field first (check both top-level and data-level)
      const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
      if (isOptional && paymentTiming) {
        if (paymentTiming === 'immediate') {
          // Pay full price when selected
          depositAmount = parseFloat(component.price) || 0;
        } else if (paymentTiming === 'with_deposit') {
          // Pay deposit amount when selected
          depositAmount = parseFloat(component.optionalDepositAmount || component.data?.optionalDepositAmount) || 0;
        } else if (paymentTiming === 'with_final') {
          // Pay with final payment - nothing due initially ($0)
          depositAmount = 0;
        } else if (paymentTiming === 'bundled') {
          // Already included in package - no additional payment ($0)
          depositAmount = 0;
        }
      } else {
        // For required components or optional components without paymentTiming, use existing logic
        // Check for deposit in component.depositAmount (Package Deal components)
        if (component.depositRequired && component.depositAmount) {
          depositAmount = parseFloat(component.depositAmount) || 0;
        }
        // Check for deposit in component.data.deposit_due (Disney components)
        else if (component.data && component.data.deposit_due) {
          depositAmount = parseFloat(component.data.deposit_due) || 0;
        }
      }
      
      console.log(`Component ${component.title}: isOptional=${isOptional}, paymentTiming=${paymentTiming}, depositRequired=${component.depositRequired}, depositAmount=${component.depositAmount}, data.deposit_due=${component.data?.deposit_due}, final=${depositAmount}`);
      return total + depositAmount;
    }, 0);
    console.log('🔍 calculateTotalDeposit result:', result);
    return result;
  };

  // Validate deposit amounts
  const validateDeposits = (components, totalPrice) => {
    const errors = [];
    let totalDeposits = 0;

    // Add safety check to ensure components is an array
    const safeComponents = Array.isArray(components) ? components : [];

    safeComponents.forEach((component, index) => {
      if (component.depositRequired) {
        const depositAmount = parseFloat(component.depositAmount) || 0;
        const componentPrice = parseFloat(component.price) || 0;
        
        if (depositAmount <= 0) {
          errors.push(`Component "${component.title}" requires a deposit amount greater than $0`);
        }
        
        if (depositAmount > componentPrice) {
          errors.push(`Component "${component.title}" deposit ($${depositAmount.toFixed(2)}) cannot exceed component price ($${componentPrice.toFixed(2)})`);
        }
        
        totalDeposits += depositAmount;
      }
    });

    if (totalDeposits > totalPrice) {
      errors.push(`Total deposits ($${totalDeposits.toFixed(2)}) cannot exceed total quote price ($${totalPrice.toFixed(2)})`);
    }

    return errors;
  };

  // Helper function to clear client selection data when quote is modified
  const clearClientSelections = () => {
    setQuote(prev => ({
      ...prev,
      options: {
        ...prev.options,
        client_selections: undefined,
        selected_total: undefined,
        selected_deposit: undefined,
        selection_timestamp: undefined
      }
    }));
  };

  // Handle component addition/edit - shared with EditItineraryModal.jsx via
  // buildSavedComponent() (see componentFormRegistry.js) so both editors
  // produce identically-shaped components.
  const handleAddComponent = (componentData) => {
    console.log('QuoteBuilder handleAddComponent called with:', componentData);

    const savedComponent = buildSavedComponent({ componentData, selectedComponentType, editingComponent });

    setQuote(prev => {
      let updatedComponents;
      if (editingComponent) {
        const oldPrice = parseFloat(editingComponent.price) || 0;
        const newPrice = savedComponent.price;
        const priceDifference = newPrice - oldPrice;
        updatedComponents = prev.components.map(comp =>
          comp.id === editingComponent.id ? savedComponent : comp
        );
        return {
          ...prev,
          components: updatedComponents,
          totalPrice: prev.totalPrice + priceDifference,
          depositAmount: calculateTotalDeposit(updatedComponents),
          options: {
            ...prev.options,
            client_selections: undefined,
            selected_total: undefined,
            selected_deposit: undefined,
            selection_timestamp: undefined
          }
        };
      }

      updatedComponents = [...prev.components, savedComponent];
      return {
        ...prev,
        components: updatedComponents,
        totalPrice: prev.totalPrice + savedComponent.price,
        depositAmount: calculateTotalDeposit(updatedComponents),
        options: {
          ...prev.options,
          client_selections: undefined,
          selected_total: undefined,
          selected_deposit: undefined,
          selection_timestamp: undefined
        }
      };
    });

    // Close modal and reset state
    console.log('Component saved successfully, closing modal');
    setShowComponentModal(false);
    setSelectedComponentType('');
    setEditingComponent(null);

    // Auto-save if editing existing quote
    if (isEditMode) {
      // Will be saved when user clicks save
    }
  };

  // Handle template selection from Component Library
  const handleSelectTemplate = (template) => {
    const componentData = {
      type: template.id,
      title: template.name,
      description: template.description,
      price: 0, // Will be set in the form
      data: template.template_data
    };
    
    setSelectedComponentType(template.id);
    setEditingComponent({ data: template.template_data });
    setShowLibraryModal(false);
    setShowComponentModal(true);
  };

  // Handle component deletion
  const handleDeleteComponent = (componentId) => {
    setQuote(prev => {
      const componentToDelete = prev.components.find(comp => comp.id === componentId);
      const updatedComponents = prev.components.filter(comp => comp.id !== componentId);
      const totalDeposit = calculateTotalDeposit(updatedComponents);
      const priceToSubtract = parseFloat(componentToDelete?.price) || 0;
      
      return {
        ...prev,
        components: updatedComponents,
        totalPrice: prev.totalPrice - priceToSubtract,
        depositAmount: totalDeposit,
        options: {
          ...prev.options,
          client_selections: undefined,
          selected_total: undefined,
          selected_deposit: undefined,
          selection_timestamp: undefined
        }
      };
    });
  };

  // Save a single quote option's payload to the API (create or update),
  // with a built-in retry if the backend rejects the request due to a
  // missing/legacy `notes` column. `isActive` controls whether the
  // component-level manual deposit override applies (that flag is tracked
  // once for whichever option is currently in view).
  const saveQuoteOptionPayload = async (id, quoteObj, sendToClient, isActive) => {
    const calculatedTotal = calculateTotalPrice(quoteObj.components);
    const calculatedDeposit = calculateTotalDeposit(quoteObj.components);
    const finalDeposit = (isActive && depositManuallyOverridden) ? quoteObj.depositAmount : calculatedDeposit;

    const quoteData = {
      title: quoteObj.quoteTitle,
      client_id: quoteObj.client?.id || null,
      client_name: `${quoteObj.client?.firstName || ''} ${quoteObj.client?.lastName || ''}`.trim(),
      client_email: quoteObj.client?.email || '',
      client_phone: quoteObj.client?.phone || '',
      travel_start_date: quoteObj.travelDates.start,
      travel_end_date: quoteObj.travelDates.end,
      adults: quoteObj.travelers.adults,
      children: quoteObj.travelers.children.length,
      components: JSON.stringify(quoteObj.components || []),
      options: JSON.stringify(quoteObj.options),
      total_amount: calculatedTotal,
      deposit_amount: finalDeposit || 0,
      deposit_due_date: quoteObj.depositDueDate || null,
      notes: quoteObj.notes || '',
      template_id: selectedTemplate || 'modern',
      status: sendToClient ? 'sent' : 'draft'
    };

    const isNew = !id;
    let response;
    try {
      response = isNew
        ? await quotesService.create(quoteData)
        : await quotesService.update(id, quoteData);
    } catch (err) {
      // If we get an error possibly related to a missing notes column, retry without it
      if (err.response?.status === 400 || err.message?.includes('notes') || err.message?.includes('column')) {
        console.warn(`Possible database schema issue with notes field, retrying without notes for quote ${id || '(new)'}`);
        const { notes, ...quoteDataWithoutNotes } = quoteData;
        response = isNew
          ? await quotesService.create(quoteDataWithoutNotes)
          : await quotesService.update(id, quoteDataWithoutNotes);
      } else {
        throw err;
      }
    }

    return { id: response.quoteId || response.id || id, response, isNew };
  };

  // Silent background save, debounced ~2.5s after the last edit (see the
  // useEffect below). Deliberately never sends to the client (sendToClient
  // is always false) and never navigates away - it only exists to persist
  // draft work-in-progress. Skipped entirely for the multi-option AI
  // comparison group, while an explicit save/send is already running, and
  // until Trip Details has actually been completed (detailsStepCompletedRef).
  const performAutosave = async () => {
    if (loading || quoteOptionGroup.length > 0 || !quote.client || !detailsStepCompletedRef.current) return;

    try {
      setSaveStatus('saving');
      const result = await saveQuoteOptionPayload(quoteId, quote, false, true);

      if (result.isNew && result.id) {
        setQuoteId(result.id);
        setQuote(prev => ({ ...prev, id: result.id }));
        window.history.replaceState(null, '', `/agent/quotes/${result.id}/edit`);
      }

      setSaveStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      setSaveStatus('error');
    }
  };

  // Shared debounce scheduling, ~2.5s after the last edit. Used both by the
  // effect below (fires on every quote change) and by the Trip Details
  // "Continue" button, so that just-completed title/dates get persisted
  // even if the agent doesn't happen to touch anything else afterward.
  const scheduleAutosave = () => {
    setSaveStatus('unsaved');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      performAutosave();
    }, 2500);
  };

  // Debounce: schedule a silent autosave ~2.5s after the agent stops
  // editing. skipNextAutosaveRef guards against firing right after a fresh
  // load/hydration (fetchQuote, AI quote generation) - those are not edits.
  useEffect(() => {
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!quote.client || quoteOptionGroup.length > 0 || !detailsStepCompletedRef.current) return;

    scheduleAutosave();

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [quote]);

  // Handle quote save. When the builder has a multi-option AI quote group
  // loaded, this snapshots the active tab's edits and then saves EVERY
  // option in the group in parallel, so agents never lose in-progress
  // edits on tabs they aren't currently viewing.
  const handleSaveQuote = async (sendToClient = false, stayOnPage = false, sendMode = 'current') => {
    setLoading(true);
    setErrors({});

    // An explicit save supersedes any pending debounced autosave.
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    const hasMultipleOptions = quoteOptionGroup.length > 0;

    // Snapshot the currently active tab's latest edits back into the group
    // before building the save list (mirrors handleSwitchOption's behavior).
    const syncedGroup = hasMultipleOptions
      ? quoteOptionGroup.map((option, idx) => (idx === activeOptionIndex ? { ...option, data: quote } : option))
      : [];
    if (hasMultipleOptions) {
      setQuoteOptionGroup(syncedGroup);
    }

    // Build the full list of quote records that need to be persisted:
    // every option in the comparison group, or just the single active quote.
    const optionsToSave = hasMultipleOptions
      ? syncedGroup.map((option, idx) => ({ id: option.id, quoteObj: option.data, optionName: option.optionName, isActive: idx === activeOptionIndex }))
      : [{ id: quoteId, quoteObj: quote, optionName: null, isActive: true }];

    // Validate deposits across every option being saved, not just the active one
    const allDepositErrors = [];
    optionsToSave.forEach(({ quoteObj, optionName }) => {
      const errs = validateDeposits(quoteObj.components, quoteObj.totalPrice);
      if (errs.length > 0) {
        const prefix = optionName ? `[${optionName}] ` : '';
        allDepositErrors.push(...errs.map(e => `${prefix}${e}`));
      }
    });
    if (allDepositErrors.length > 0) {
      setErrors({ deposit: allDepositErrors.join('. ') });
      setLoading(false);
      return;
    }

    console.log(`💾 Saving ${optionsToSave.length} quote option(s)...`);

    try {
      // Save every option in parallel, and wait for all of them to settle
      // (success or failure) before releasing the loading state.
      const settled = await Promise.allSettled(
        optionsToSave.map(({ id, quoteObj, isActive }) => saveQuoteOptionPayload(id, quoteObj, sendToClient, isActive))
      );

      const failedIndexes = settled
        .map((result, idx) => (result.status === 'rejected' ? idx : null))
        .filter(idx => idx !== null);

      if (failedIndexes.length > 0) {
        const failedLabels = failedIndexes
          .map(idx => optionsToSave[idx].optionName || (hasMultipleOptions ? `Option ${idx + 1}` : 'Quote'))
          .join(', ');
        const firstError = settled[failedIndexes[0]].reason;
        const detail = firstError?.response?.data?.error || firstError?.message || 'Unknown error';
        throw new Error(`Failed to save: ${failedLabels}. ${detail}`);
      }

      const results = settled.map(result => result.value);

      // Update ids/state for any newly-created quotes
      results.forEach((result, idx) => {
        if (result.isNew && (result.response.quoteId || result.response.id)) {
          const newQuoteId = result.response.quoteId || result.response.id;

          if (hasMultipleOptions) {
            setQuoteOptionGroup(prev => prev.map((option, i) => (i === idx ? { ...option, id: newQuoteId } : option)));
            if (idx === activeOptionIndex) {
              setQuoteId(newQuoteId);
              skipNextAutosaveRef.current = true;
              setQuote(prev => ({ ...prev, id: newQuoteId }));
            }
          } else {
            setQuoteId(newQuoteId);
            skipNextAutosaveRef.current = true;
            setQuote(prev => ({ ...prev, id: newQuoteId }));
            if (stayOnPage) {
              window.history.replaceState(null, '', `/agent/quotes/${newQuoteId}/edit`);
            }
          }
        }
      });

      // This explicit save just persisted everything the debounced
      // autosave would have - reflect that in the status indicator.
      setSaveStatus('saved');

      const activeResult = hasMultipleOptions ? results[activeOptionIndex] : results[0];
      const activeIdForSend = activeResult?.id || quoteId;

      if (sendToClient && activeIdForSend) {
        // Send email to client. sendMode='comparison' emails a link to the
        // tabbed comparison page covering every option; 'current' (default)
        // emails a link to just the option currently being viewed.
        const sendResponse = await quotesService.send(activeIdForSend, { sendMode });
        const wasComparisonSend = sendResponse?.isComparisonSend;
        toast.success(
          wasComparisonSend
            ? `Comparison of all ${sendResponse.optionCount} options sent successfully to ${quote.client?.email}`
            : 'Quote sent successfully to ' + quote.client?.email +
              (hasMultipleOptions ? ` (all ${results.length} options saved)` : '')
        );
      } else if (!sendToClient) {
        toast.success(
          hasMultipleOptions
            ? `All ${results.length} quote options saved successfully!`
            : (isEditMode ? 'Quote updated successfully!' : 'Quote saved as draft!')
        );
      }

      // Only navigate away if not staying on page
      if (!stayOnPage) {
        navigate('/agent/quotes');
      }

      return activeResult?.response; // Return the active option's response for further use
    } catch (error) {
      console.error('Error saving quote:', error);
      let errorMessage = 'Failed to save quote. Please try again.';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Known "primary date" field names across the component forms (camelCase
  // for general forms, snake_case for the brand package forms). Used to
  // auto-clear the "dates not confirmed yet" reminder below once the agent
  // has actually filled in a real date for the component.
  const PRIMARY_DATE_FIELD_KEYS = [
    'checkInDate', 'check_in_date', 'departureDate', 'departure_date',
    'pickupDate', 'activityDate', 'date', 'reservationDate'
  ];

  // Voyager AI concierge leads only ever capture a freeform `timeframe`
  // (e.g. "June 2027"), never exact dates - see
  // backend/utils/conciergeLeadToQuoteComponent.js. This surfaces that as a
  // reminder on the component card so the agent doesn't forget to pick
  // real dates before sending, without ever exposing it to the client
  // (requestedTimeframe/needsDatesConfirmed are agent-only fields that
  // ModernQuoteView.js does not read).
  const needsDateReminder = (component) => {
    if (!component?.needsDatesConfirmed || !component?.requestedTimeframe) return false;
    const data = component.data || {};
    return !PRIMARY_DATE_FIELD_KEYS.some((key) => !!data[key]);
  };

  // Render component form based on type
  const renderComponentForm = () => {
    const initialData = getComponentFormInitialData(editingComponent);

    const props = {
      onSave: handleAddComponent,
      onCancel: () => {
        setShowComponentModal(false);
        setSelectedComponentType('');
        setEditingComponent(null);
      },
      initialData: initialData
    };

    // If selectedComponentType is missing but we have editingComponent, try to infer it
    let effectiveComponentType = selectedComponentType;
    if (!effectiveComponentType && editingComponent) {
      effectiveComponentType = editingComponent.type || inferComponentType(editingComponent);
    }

    return renderComponentTypeForm(effectiveComponentType, props);
  };

  if (loadingQuote) {
    return (
      <div className="loading-container">
        <div className="loading">Loading quote...</div>
      </div>
    );
  }

  return (
    <div className="quote-builder">
      {/* Header */}
      <div className="quote-builder-header">
        <h1>{isEditMode ? 'Edit Quote' : 'Create New Quote'}</h1>
        {saveStatus !== 'idle' && quoteOptionGroup.length === 0 && (
          <span className={`autosave-status autosave-status-${saveStatus}`}>
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && '✓ All changes saved'}
            {saveStatus === 'unsaved' && 'Unsaved changes'}
            {saveStatus === 'error' && '⚠ Save failed'}
          </span>
        )}
      </div>

      {/* Multi-Option AI Quote Comparison Tabs */}
      <QuoteOptionTabs
        options={quoteOptionGroup.map((option, idx) => ({
          id: option.id,
          optionName: option.optionName,
          // Show live totals for the active tab (in case the agent is mid-edit),
          // and the last-synced totals for inactive tabs.
          totalPrice: idx === activeOptionIndex ? quote.totalPrice : option.data?.totalPrice
        }))}
        activeIndex={activeOptionIndex}
        onSelect={handleSwitchOption}
      />

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-12 px-4">
        <div className="flex items-center w-full max-w-4xl">
          {/* Step 1 */}
          <div className="flex flex-col items-center flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              activeStep === 'client' 
                ? 'bg-blue-600 text-white shadow-lg scale-110' 
                : quote.client 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {quote.client ? <Check size={24} /> : '1'}
            </div>
            <span className={`mt-2 text-sm font-medium transition-colors ${
              activeStep === 'client' ? 'text-blue-600' : quote.client ? 'text-green-600' : 'text-gray-400'
            }`}>
              Select Client
            </span>
          </div>
          
          {/* Connector Line */}
          <div className={`flex-1 h-1 mx-2 transition-colors ${
            quote.client ? 'bg-green-500' : 'bg-gray-200'
          }`} />
          
          {/* Step 2 */}
          <div className="flex flex-col items-center flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              activeStep === 'details' 
                ? 'bg-blue-600 text-white shadow-lg scale-110' 
                : quote.travelDates.start 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {quote.travelDates.start ? <Check size={24} /> : '2'}
            </div>
            <span className={`mt-2 text-sm font-medium transition-colors ${
              activeStep === 'details' ? 'text-blue-600' : quote.travelDates.start ? 'text-green-600' : 'text-gray-400'
            }`}>
              Trip Details
            </span>
          </div>
          
          {/* Connector Line */}
          <div className={`flex-1 h-1 mx-2 transition-colors ${
            (Array.isArray(quote.components) ? quote.components : []).length > 0 ? 'bg-green-500' : 'bg-gray-200'
          }`} />
          
          {/* Step 3 */}
          <div className="flex flex-col items-center flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              activeStep === 'components' 
                ? 'bg-blue-600 text-white shadow-lg scale-110' 
                : (Array.isArray(quote.components) ? quote.components : []).length > 0 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {(Array.isArray(quote.components) ? quote.components : []).length > 0 ? <Check size={24} /> : '3'}
            </div>
            <span className={`mt-2 text-sm font-medium transition-colors ${
              activeStep === 'components' ? 'text-blue-600' : (Array.isArray(quote.components) ? quote.components : []).length > 0 ? 'text-green-600' : 'text-gray-400'
            }`}>
              Add Components
            </span>
          </div>
          
          {/* Connector Line */}
          <div className="flex-1 h-1 mx-2 bg-gray-200" />
          
          {/* Step 4 */}
          <div className="flex flex-col items-center flex-1">
            <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-lg transition-all duration-300 ${
              activeStep === 'preview' 
                ? 'bg-blue-600 text-white shadow-lg scale-110' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              4
            </div>
            <span className={`mt-2 text-sm font-medium transition-colors ${
              activeStep === 'preview' ? 'text-blue-600' : 'text-gray-400'
            }`}>
              Review & Send
            </span>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="quote-builder-content">
        {/* Client Selection Step */}
        {activeStep === 'client' && (
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Select Client</h2>
            
            {/* Action Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Select Existing Client Card */}
              <button
                onClick={() => setShowClientModal(true)}
                className="group bg-white border-2 border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md hover:border-blue-500 transition-all duration-300 text-left"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                    <UserSearch className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      Select Existing Client
                    </h3>
                    <p className="text-sm text-gray-500">
                      Search your CRM for an existing client
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>
              </button>

              {/* Create New Client Card */}
              <button
                onClick={() => {
                  setShowClientModal(true);
                  // Set flag for new client form
                }}
                className="group bg-white border-2 border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md hover:border-green-500 transition-all duration-300 text-left"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-500 transition-colors">
                    <UserPlus className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2 group-hover:text-green-600 transition-colors">
                      Create New Client
                    </h3>
                    <p className="text-sm text-gray-500">
                      Add a fresh lead to your CRM
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                </div>
              </button>
            </div>

            {/* Selected Client Display */}
            {quote.client && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-md">
                      {quote.client.firstName?.[0]}{quote.client.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-1">
                        {quote.client.firstName} {quote.client.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">Selected Client</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                      <Check size={16} />
                      <span>Confirmed</span>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Email</p>
                    <p className="text-gray-800 font-medium">{quote.client.email}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Phone</p>
                    <p className="text-gray-800 font-medium">{quote.client.phone}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => setActiveStep('details')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                >
                  <span>Continue to Trip Details</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Trip Details Step */}
        {activeStep === 'details' && (
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Trip Details</h2>
            <form className="space-y-8">
              {/* Quote Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Quote Title*
                </label>
                <input
                  type="text"
                  value={quote.quoteTitle}
                  onChange={(e) => setQuote(prev => ({ ...prev, quoteTitle: e.target.value }))}
                  placeholder="e.g., Smith Family Caribbean Cruise"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800 placeholder-gray-400"
                  required
                />
              </div>

              {/* Travel Dates - Visual Container */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Travel Dates</h3>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date*
                    </label>
                    <input
                      type="date"
                      value={quote.travelDates.start}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelDates: { ...prev.travelDates, start: e.target.value }
                      }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date*
                    </label>
                    <input
                      type="date"
                      value={quote.travelDates.end}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelDates: { ...prev.travelDates, end: e.target.value }
                      }))}
                      min={quote.travelDates.start}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-gray-800"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Travelers Section */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <Users className="w-5 h-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Travelers</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Adults*
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quote.travelers.adults}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelers: { ...prev.travelers, adults: parseInt(e.target.value) }
                      }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Children
                    </label>
                    <button 
                      type="button"
                      onClick={() => {
                        setQuote(prev => ({
                          ...prev,
                          travelers: {
                            ...prev.travelers,
                            children: [...prev.travelers.children, { age: 0 }]
                          }
                        }));
                      }}
                      className="w-full border-dashed border-2 border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50 rounded-lg py-3 px-4 flex items-center justify-center space-x-2 transition-all duration-200"
                    >
                      <Plus size={20} />
                      <span className="font-medium">Add Child</span>
                    </button>
                  </div>
                </div>

                {/* Children List */}
                {quote.travelers.children.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Children Ages</p>
                    {quote.travelers.children.map((child, index) => (
                      <div key={index} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                        <label className="text-sm font-medium text-gray-700 flex-shrink-0">
                          Child {index + 1}:
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="17"
                          value={child.age}
                          onChange={(e) => {
                            const newChildren = [...quote.travelers.children];
                            newChildren[index] = { age: parseInt(e.target.value) };
                            setQuote(prev => ({
                              ...prev,
                              travelers: { ...prev.travelers, children: newChildren }
                            }));
                          }}
                          className="w-24 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-800"
                        />
                        <span className="text-sm text-gray-500">years old</span>
                        <button
                          type="button"
                          onClick={() => {
                            setQuote(prev => ({
                              ...prev,
                              travelers: {
                                ...prev.travelers,
                                children: prev.travelers.children.filter((_, i) => i !== index)
                              }
                            }));
                          }}
                          className="ml-auto px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-sm font-medium transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between pt-6 border-t-2 border-gray-200">
                <button 
                  type="button"
                  onClick={() => setActiveStep('client')}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors duration-200"
                >
                  Back
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    // Trip Details is complete (button is disabled otherwise) -
                    // let autosave persist it now rather than waiting on the
                    // agent to happen to touch something else afterward.
                    detailsStepCompletedRef.current = true;
                    scheduleAutosave();
                    setActiveStep('components');
                  }}
                  disabled={!quote.quoteTitle || !quote.travelDates.start || !quote.travelDates.end}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
                >
                  <span>Continue</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Components Step */}
        {activeStep === 'components' && (
          <div className="step-content">
            <h2>Travel Components</h2>
            
            <div className="component-types">
              <div className="components-section">
                <div className="section-header">
                  <h3>Add Component:</h3>
                  <div className="add-component-buttons">
                    {/* Temporarily hidden - incomplete features
                    {user?.email === 'chapmaninvestmentgroupllc@gmail.com' && (
                      <>
                        <button
                          className="ai-quote-builder-btn"
                          onClick={() => setShowConversationalBuilder(true)}
                        >
                          🤖 AI Quote Builder
                        </button>
                        <button
                          className="ai-quote-builder-btn secondary"
                          onClick={() => setShowPDFUploadModal(true)}
                        >
                          📄 Quick PDF Upload
                        </button>
                      </>
                    )}
                    */}
                    <button
                      className="component-library-btn"
                      onClick={() => setShowLibraryModal(true)}
                    >
                      📚 Component Library
                    </button>
                  </div>
                </div>
                
                <div className="component-categories">
                  {componentCategories.map(category => (
                    <div key={category.id} className="category-section">
                      <div className="category-header">
                        <span className="category-icon">{category.icon}</span>
                        <div className="category-info">
                          <h4 className="category-name">{category.name}</h4>
                          <p className="category-description">{category.description}</p>
                        </div>
                      </div>
                      <div className="category-components">
                        {category.components.map(type => (
                          <button
                            key={type.id}
                            className="component-type-btn"
                            onClick={() => {
                              setSelectedComponentType(type.id);
                              setShowComponentModal(true);
                            }}
                          >
                            <span className="component-icon">{type.icon}</span>
                            <span className="component-name">{type.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {(Array.isArray(quote.components) ? quote.components : []).length > 0 && (
              <div className="components-list">
                <h3>Added Components:</h3>
                {(Array.isArray(quote.components) ? quote.components : [])
                  .filter(component => {
                    return component && component.title && component.title !== 'undefined' && component.title.trim() !== '';
                  })
                  .map(component => {
                    // Check for isOption in both locations for compatibility
                    const isOption = component.isOption || component.data?.isOption || false;
                    const optionDescription = component.optionDescription || component.data?.optionDescription || '';
                    return (
                      <div key={component.id} className={`component-card ${isOption ? 'optional-component' : 'required-component'}`}>
                    <div className="component-header">
                      <div className="component-title-section">
                        <h4>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</h4>
                        {isOption && (
                          <span className="optional-badge">Optional</span>
                        )}
                      </div>
                      <div className="component-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            console.log('Edit button clicked - component:', component);
                            console.log('Edit button clicked - component.type:', component.type);
                            const componentType = component.type || inferComponentType(component);
                            console.log('Edit button clicked - using componentType:', componentType);
                            setEditingComponent(component);
                            setSelectedComponentType(componentType);
                            setShowComponentModal(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteComponent(component.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="component-details">
                      {needsDateReminder(component) && (
                        <p className="needs-dates-warning">
                          🗓️ Client requested <strong>{component.requestedTimeframe}</strong> - exact dates not selected yet
                        </p>
                      )}
                      <p>{component.description}</p>
                      {isOption && optionDescription && (
                        <p className="option-description">
                          <strong>Client sees:</strong> {optionDescription}
                        </p>
                      )}
                      {isOption && component.paymentTiming && (
                        <p className="payment-timing-info">
                          <strong>Payment:</strong> {
                            component.paymentTiming === 'immediate' ? '💳 Due immediately when selected' :
                            component.paymentTiming === 'with_deposit' ? `💰 $${parseFloat(component.optionalDepositAmount || 0).toFixed(2)} deposit when selected` :
                            component.paymentTiming === 'bundled' ? '📦 Bundled with package' :
                            '📅 Due with final payment'
                          }
                        </p>
                      )}
                      <p className="component-price">
                        {component.includedInPackage ? (
                          <span className="included-in-package-badge">Included in Package Price</span>
                        ) : (
                          `$${(parseFloat(component.price) || 0).toFixed(2)}`
                        )}
                      </p>
                    </div>
                      </div>
                    );
                  })}
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveStep('details')}
              >
                Back
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={() => setActiveStep('preview')}
                disabled={(Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                Continue to Preview
              </button>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {activeStep === 'preview' && (
          <div className="step-content">
            <h2>Review Quote</h2>
            
            {/* Template Selector */}
            <div className="template-selector" style={{
              marginBottom: '30px',
              padding: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '12px',
              border: '1px solid #cbd5e1'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🎨 Choose Quote Template
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px'
              }}>
                {/* Modern Template */}
                <div
                  onClick={() => setSelectedTemplate('modern')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'modern' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'modern' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'modern' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'modern' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>✨</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Modern</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Clean & Professional</div>
                  {selectedTemplate === 'modern' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Magical Template */}
                <div
                  onClick={() => setSelectedTemplate('magical')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'magical' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'magical' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'magical' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'magical' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>🏰</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Magical</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Disney & Theme Parks</div>
                  {selectedTemplate === 'magical' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Modern Agency Template */}
                <div
                  onClick={() => setSelectedTemplate('modern_agency')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'modern_agency' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'modern_agency' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'modern_agency' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'modern_agency' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>🏢</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Modern Agency</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Corporate Timeline</div>
                  {selectedTemplate === 'modern_agency' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Editorial Magazine Template */}
                <div
                  onClick={() => setSelectedTemplate('editorial_magazine')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'editorial_magazine' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'editorial_magazine' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'editorial_magazine' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'editorial_magazine' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>📖</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Editorial</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Luxury Magazine</div>
                  {selectedTemplate === 'editorial_magazine' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Expedition Adventure Template */}
                <div
                  onClick={() => setSelectedTemplate('expedition_adventure')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'expedition_adventure' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'expedition_adventure' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'expedition_adventure' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'expedition_adventure' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>⛰️</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Expedition</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Adventure Trail</div>
                  {selectedTemplate === 'expedition_adventure' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Executive Brief Template */}
                <div
                  onClick={() => setSelectedTemplate('executive_brief')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'executive_brief' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'executive_brief' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'executive_brief' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'executive_brief' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>📋</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Executive Brief</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Corporate Minimal</div>
                  {selectedTemplate === 'executive_brief' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Studio Action Template */}
                <div
                  onClick={() => setSelectedTemplate('studio_action')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'studio_action' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'studio_action' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'studio_action' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'studio_action' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>🎬</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Studio Action</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Universal Studios</div>
                  {selectedTemplate === 'studio_action' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Luxury River Template */}
                <div
                  onClick={() => setSelectedTemplate('luxury_river')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'luxury_river' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'luxury_river' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'luxury_river' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'luxury_river' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>⚓</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Luxury River</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Cruise Elegance</div>
                  {selectedTemplate === 'luxury_river' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Tropical Escape Template */}
                <div
                  onClick={() => setSelectedTemplate('tropical_escape')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'tropical_escape' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'tropical_escape' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'tropical_escape' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'tropical_escape' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>🏝️</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Tropical Escape</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Beach & Resort</div>
                  {selectedTemplate === 'tropical_escape' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Boarding Pass Template */}
                <div
                  onClick={() => setSelectedTemplate('boarding_pass')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'boarding_pass' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'boarding_pass' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'boarding_pass' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'boarding_pass' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>✈️</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Boarding Pass</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Flight Only</div>
                  {selectedTemplate === 'boarding_pass' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>

                {/* Destination Wedding Template */}
                <div
                  onClick={() => setSelectedTemplate('destination_wedding')}
                  style={{
                    padding: '20px',
                    borderRadius: '10px',
                    border: selectedTemplate === 'destination_wedding' ? '3px solid #2563eb' : '2px solid #cbd5e1',
                    background: selectedTemplate === 'destination_wedding' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedTemplate === 'destination_wedding' ? '0 4px 12px rgba(37, 99, 235, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
                    transform: selectedTemplate === 'destination_wedding' ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px', textAlign: 'center' }}>💒</div>
                  <div style={{ fontWeight: '600', color: '#1e293b', textAlign: 'center', marginBottom: '4px' }}>Wedding</div>
                  <div style={{ fontSize: '12px', color: '#64748b', textAlign: 'center' }}>Destination Romance</div>
                  {selectedTemplate === 'destination_wedding' && (
                    <div style={{ marginTop: '10px', textAlign: 'center', color: '#2563eb', fontSize: '14px', fontWeight: '600' }}>✓ Selected</div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Quote Summary */}
            <div className="quote-summary">
              <div className="quote-header">
                <h3>{quote.quoteTitle}</h3>
                <div className="quote-details">
                  <p><strong>Client:</strong> {quote.client?.firstName} {quote.client?.lastName}</p>
                  <p><strong>Email:</strong> {quote.client?.email}</p>
                  <p><strong>Travel Dates:</strong> {quote.travelDates.start} to {quote.travelDates.end}</p>
                  <p><strong>Travelers:</strong> {quote.travelers.adults} adults{quote.travelers.children.length > 0 && `, ${quote.travelers.children.length} children`}</p>
                </div>
              </div>

              {/* Components List */}
              <div className="components-preview">
                <h4>Trip Components</h4>
                {(() => {
                  const clientSelections = quote.options?.client_selections || {};
                  const hasClientSelections = Object.keys(clientSelections).length > 0;
                  
                  return (Array.isArray(quote.components) ? quote.components : []).map(component => {
                    const isSelected = clientSelections[component.id];
                    const isOptional = component.isOption || component.data?.isOption || false;
                    const optionDescription = component.optionDescription || component.data?.optionDescription || '';
                    
                    return (
                      <div key={component.id} className={`component-preview ${isOptional ? 'optional-component' : 'required-component'} ${hasClientSelections && isOptional ? (isSelected ? 'client-selected' : 'client-not-selected') : ''}`}>
                        <div className="component-header">
                          <div className="component-title-section">
                            <span>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</span>
                            {isOptional && (
                              <div className="component-status-badges">
                                <span className="optional-badge">Optional</span>
                                {hasClientSelections && (
                                  <span className={`selection-badge ${isSelected ? 'selected' : 'not-selected'}`}>
                                    {isSelected ? '✓ Client Selected' : '✗ Not Selected'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="component-price">${(parseFloat(component.price) || 0).toFixed(2)}</span>
                        </div>
                        {needsDateReminder(component) && (
                          <p className="needs-dates-warning">
                            🗓️ Client requested <strong>{component.requestedTimeframe}</strong> - exact dates not selected yet
                          </p>
                        )}
                        {component.description && <p>{component.description}</p>}
                        {isOptional && optionDescription && (
                          <p className="option-description">
                            <strong>Client sees:</strong> {optionDescription}
                          </p>
                        )}
                      </div>
                    );
                  });
                })()}
                
                {(() => {
                  const clientSelections = quote.options?.client_selections;
                  if (clientSelections && Object.keys(clientSelections).length > 0) {
                    const selectionTimestamp = quote.options?.selection_timestamp;
                    return (
                      <div className="client-selections-summary">
                        <h5>📋 Client Selection Summary</h5>
                        <p>
                          <strong>Last updated:</strong> {selectionTimestamp ? new Date(selectionTimestamp).toLocaleString() : 'Unknown'}
                        </p>
                        {quote.options?.selected_total && (
                          <div className="selection-totals">
                            <p><strong>Selected Total:</strong> ${quote.options.selected_total.toFixed(2)}</p>
                            <p><strong>Initial Payment Due:</strong> ${quote.options.selected_deposit?.toFixed(2) || '0.00'}</p>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Pricing Summary */}
              <div className="pricing-summary">
                <div className="total-price">
                  <strong>Total: ${(quote.totalPrice || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                </div>
              </div>
            </div>
            <div className="agent-edit-sections">
              <div className="deposit-section">
                <h3>Deposit Information</h3>
                <div className="deposit-options">
                  <div className="form-group">
                    <label>Deposit Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={quote.depositAmount || ''}
                      onChange={(e) => {
                        setDepositManuallyOverridden(true);
                        setQuote(prev => ({ 
                          ...prev, 
                          depositAmount: parseFloat(e.target.value) || 0
                        }));
                      }}
                    />
                    <small style={{color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block'}}>
                      {depositManuallyOverridden ? (
                        <>
                          🔒 <strong>Manual Override Active</strong> - Deposit will not auto-update when components change.
                          <button
                            type="button"
                            onClick={() => {
                              setDepositManuallyOverridden(false);
                              setQuote(prev => ({ 
                                ...prev, 
                                depositAmount: calculateTotalDeposit(prev.components)
                              }));
                            }}
                            style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              fontSize: '0.85em',
                              background: '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Reset to Auto-Calculate
                          </button>
                        </>
                      ) : (
                        '💡 Auto-calculated from component payment timing settings. You can override if needed.'
                      )}
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Deposit Due Date</label>
                    <input
                      type="date"
                      value={quote.depositDueDate}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        depositDueDate: e.target.value
                      }))}
                    />
                  </div>
                </div>
              </div>

              <div className="notes-section">
                <label>Additional Notes</label>
                <textarea
                  value={quote.notes}
                  onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information for the client..."
                  rows="4"
                />
              </div>
            </div>

            {/* Live Template Preview */}
            <div style={{
              marginTop: '40px',
              marginBottom: '30px',
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '2px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                👁️ Live Preview - {
                  selectedTemplate === 'modern' ? 'Modern' :
                  selectedTemplate === 'magical' ? 'Magical' :
                  selectedTemplate === 'modern_agency' ? 'Modern Agency' :
                  selectedTemplate === 'editorial_magazine' ? 'Editorial Magazine' :
                  selectedTemplate === 'expedition_adventure' ? 'Expedition Adventure' :
                  selectedTemplate === 'executive_brief' ? 'Executive Brief' :
                  selectedTemplate === 'studio_action' ? 'Studio Action' :
                  selectedTemplate === 'luxury_river' ? 'Luxury River' :
                  selectedTemplate === 'tropical_escape' ? 'Tropical Escape' :
                  selectedTemplate === 'boarding_pass' ? 'Boarding Pass' :
                  selectedTemplate === 'destination_wedding' ? 'Destination Wedding' :
                  'Modern'
                } Template
              </h3>
              <div style={{
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                overflow: 'hidden',
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                <QuoteRenderer 
                  key={selectedTemplate}
                  quoteData={{
                    quoteTitle: quote.quoteTitle,
                    quoteNumber: 'PREVIEW',
                    client: {
                      firstName: quote.client?.firstName || '',
                      lastName: quote.client?.lastName || '',
                      email: quote.client?.email || '',
                      phone: quote.client?.phone || ''
                    },
                    travelDates: {
                      start: quote.travelDates.start,
                      end: quote.travelDates.end
                    },
                    travelers: {
                      adults: quote.travelers.adults || 1,
                      children: quote.travelers.children || []
                    },
                    components: quote.components || [],
                    totalPrice: quote.totalPrice || 0,
                    depositAmount: quote.depositAmount || 0,
                    depositDueDate: quote.depositDueDate,
                    notes: quote.notes || '',
                    agent_name: user?.name || '',
                    agent_email: user?.email || '',
                    agent_phone: user?.phone || '',
                    agent_business_name: user?.business_name || '',
                    agent_logo_url: user?.logo_url || ''
                  }}
                  templateId={selectedTemplate}
                  isAgentPreview={true}
                />
              </div>
            </div>

            <div className="form-actions preview-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveStep('components')}
              >
                Back
              </button>
              
              <button 
                type="button"
                className="btn btn-info preview-quote-btn"
                onClick={async () => {
                  try {
                    let currentQuoteId = quote.id || quoteId; // Use quote.id or state quoteId
                    
                    if (!currentQuoteId) {
                      // New quote - must save first, but stay on page
                      const response = await handleSaveQuote(false, true); // Save as draft, stay on page
                      currentQuoteId = response.quoteId || response.id;
                      
                      if (!currentQuoteId) {
                        toast.error('Failed to save quote. Please try again.');
                        return;
                      }
                    } else {
                      // Existing quote - auto-save current changes before preview, stay on page
                      await handleSaveQuote(false, true); // Save as draft, stay on page
                    }
                    
                    // Open preview after successful save
                    const previewUrl = `/agent/quotes/${currentQuoteId}/preview`;
                    window.open(previewUrl, '_blank', 'width=1200,height=800');
                  } catch (error) {
                    console.error('Error saving quote before preview:', error);
                    toast.error('Failed to save quote. Please try again.');
                  }
                }}
                disabled={loading || !quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                {loading ? 'Saving...' : 'Preview Quote'}
              </button>
              
              <button 
                type="button"
                className="btn btn-secondary"
                style={{ display: 'none' }}
                onClick={() => {
                  // Open preview in new window
                  const previewWindow = window.open('', '_blank', 'width=800,height=600');
                  const previewContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>Quote Preview - ${quote.quoteTitle}</title>
                      <style>
                        body { 
                          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                          max-width: 900px; 
                          margin: 0 auto; 
                          padding: 40px 30px; 
                          background: #f8fafc;
                          color: #334155;
                          line-height: 1.6;
                        }
                        .quote-container {
                          background: white;
                          border-radius: 12px;
                          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                          overflow: hidden;
                        }
                        .header { 
                          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                          color: white;
                          padding: 40px 30px;
                          text-align: center;
                        }
                        .header h1 { 
                          margin: 0 0 10px 0; 
                          font-size: 2.5em; 
                          font-weight: 700;
                          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        .header p { 
                          margin: 5px 0; 
                          font-size: 1.1em;
                          opacity: 0.9;
                        }
                        .content {
                          padding: 30px;
                        }
                        .section { 
                          margin-bottom: 35px; 
                          padding-bottom: 25px;
                          border-bottom: 1px solid #e2e8f0;
                        }
                        .section:last-child {
                          border-bottom: none;
                          margin-bottom: 0;
                        }
                        .section h2 { 
                          color: #1e40af; 
                          margin: 0 0 20px 0;
                          font-size: 1.5em;
                          font-weight: 600;
                          display: flex;
                          align-items: center;
                          gap: 10px;
                        }
                        .section h2:before {
                          content: '';
                          width: 4px;
                          height: 24px;
                          background: #2563eb;
                          border-radius: 2px;
                        }
                        .detail-row {
                          display: flex;
                          justify-content: space-between;
                          margin-bottom: 8px;
                          padding: 8px 0;
                        }
                        .detail-label {
                          font-weight: 600;
                          color: #475569;
                        }
                        .detail-value {
                          color: #1e293b;
                        }
                        .component { 
                          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                          padding: 20px; 
                          margin-bottom: 15px; 
                          border-radius: 10px;
                          border-left: 4px solid #2563eb;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                        }
                        .component h3 { 
                          margin: 0 0 10px 0; 
                          color: #1e40af;
                          font-size: 1.2em;
                          font-weight: 600;
                        }
                        .component p { 
                          margin: 8px 0; 
                          color: #475569;
                        }
                        .component-price { 
                          font-weight: 700; 
                          color: #059669;
                          font-size: 1.1em;
                          text-align: right;
                          margin-top: 10px;
                        }
                        .pricing-summary {
                          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                          padding: 25px;
                          border-radius: 10px;
                          border: 2px solid #0ea5e9;
                          margin-top: 20px;
                        }
                        .pricing-row {
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          margin-bottom: 12px;
                          padding: 8px 0;
                        }
                        .pricing-row.total {
                          border-top: 2px solid #0ea5e9;
                          padding-top: 15px;
                          margin-top: 15px;
                          font-size: 1.3em;
                          font-weight: 700;
                        }
                        .pricing-label {
                          font-weight: 600;
                          color: #0c4a6e;
                        }
                        .pricing-value {
                          font-weight: 700;
                          color: #059669;
                          font-size: 1.1em;
                        }
                        .pricing-row.total .pricing-value {
                          font-size: 1.4em;
                          color: #0c4a6e;
                        }
                        .attachments-section {
                          margin: 15px 0;
                          padding: 10px 0;
                          border-top: 1px solid #e2e8f0;
                        }
                        .attachments-section h4 {
                          color: #1e40af;
                          margin: 15px 0 8px 0;
                          font-size: 1.1em;
                          font-weight: 600;
                        }
                        .attachments-section img {
                          max-width: 200px;
                          max-height: 150px;
                          margin: 5px;
                          border-radius: 5px;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        .attachments-section ul {
                          margin: 8px 0;
                          padding-left: 20px;
                        }
                        .attachments-section li {
                          margin: 4px 0;
                        }
                        .footer {
                          text-align: center;
                          padding: 20px;
                          background: #f8fafc;
                          color: #64748b;
                          font-size: 0.9em;
                        }
                        @media print {
                          body { background: white; }
                          .quote-container { box-shadow: none; }
                        }
                      </style>
                    </head>
                    <body>
                      <div class="quote-container">
                        <div class="header">
                          <h1>${quote.quoteTitle}</h1>
                          <p><strong>Prepared for:</strong> ${quote.client?.firstName} ${quote.client?.lastName}</p>
                          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
                        </div>
                        
                        <div class="content">
                          <div class="section">
                            <h2>Trip Details</h2>
                            <div class="detail-row">
                              <span class="detail-label">Travel Dates:</span>
                              <span class="detail-value">${quote.travelDates.start} to ${quote.travelDates.end}</span>
                            </div>
                            <div class="detail-row">
                              <span class="detail-label">Travelers:</span>
                              <span class="detail-value">${quote.travelers.adults} Adult${quote.travelers.adults !== 1 ? 's' : ''}${quote.travelers.children.length > 0 ? `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}` : ''}</span>
                            </div>
                          </div>
                          
                          <div class="section">
                            <h2>Travel Components</h2>
                            ${(Array.isArray(quote.components) ? quote.components : [])
                              .filter(comp => comp && comp.title && comp.title !== 'undefined' && comp.title.trim() !== '')
                              .map(comp => {
                                // Helper function to render component dates
                                const renderDates = (component) => {
                                  let dateInfo = '';
                                  
                                  // Flight dates
                                  if (component.departureDate) {
                                    dateInfo += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Departure' : 'Departure'}:</strong> ${component.departureDate}${component.departureTime ? ` at ${component.departureTime}` : ''}</p>`;
                                  }
                                  if (component.arrivalDate) {
                                    dateInfo += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Arrival' : 'Arrival'}:</strong> ${component.arrivalDate}${component.arrivalTime ? ` at ${component.arrivalTime}` : ''}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnDepartureDate) {
                                    dateInfo += `<p><strong>Return Departure:</strong> ${component.returnDepartureDate}${component.returnDepartureTime ? ` at ${component.returnDepartureTime}` : ''}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnArrivalDate) {
                                    dateInfo += `<p><strong>Return Arrival:</strong> ${component.returnArrivalDate}${component.returnArrivalTime ? ` at ${component.returnArrivalTime}` : ''}</p>`;
                                  }
                                  
                                  // Hotel dates
                                  if (component.checkInDate) {
                                    dateInfo += `<p><strong>Check-in:</strong> ${component.checkInDate}</p>`;
                                  }
                                  if (component.checkOutDate) {
                                    dateInfo += `<p><strong>Check-out:</strong> ${component.checkOutDate}</p>`;
                                  }
                                  
                                  // Car rental dates
                                  if (component.pickupDate) {
                                    dateInfo += `<p><strong>Pickup:</strong> ${component.pickupDate}${component.pickupTime ? ` at ${component.pickupTime}` : ''}</p>`;
                                  }
                                  if (component.dropoffDate) {
                                    dateInfo += `<p><strong>Drop-off:</strong> ${component.dropoffDate}${component.dropoffTime ? ` at ${component.dropoffTime}` : ''}</p>`;
                                  }
                                  
                                  // Insurance policy dates
                                  if (component.policyStartDate) {
                                    dateInfo += `<p><strong>Policy Start:</strong> ${component.policyStartDate}</p>`;
                                  }
                                  if (component.policyEndDate) {
                                    dateInfo += `<p><strong>Policy End:</strong> ${component.policyEndDate}</p>`;
                                  }
                                  
                                  // Activity/Restaurant/General dates
                                  if (component.date) {
                                    dateInfo += `<p><strong>Date:</strong> ${component.date}${component.time ? ` at ${component.time}` : ''}</p>`;
                                  }
                                  
                                  return dateInfo;
                                };
                                
                                // Helper function to render component details
                                const renderDetails = (component) => {
                                  let details = '';
                                  
                                  // Flight details
                                  if (component.airline) {
                                    details += `<p><strong>Airline:</strong> ${component.airline}${component.flightNumber ? ` (${component.flightNumber})` : ''}</p>`;
                                  }
                                  if (component.departureAirport || component.arrivalAirport) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Route' : 'Route'}:</strong> ${component.departureAirport || 'N/A'} → ${component.arrivalAirport || 'N/A'}</p>`;
                                  }
                                  if (component.seats) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Seats' : 'Seats'}:</strong> ${component.seats}</p>`;
                                  }
                                  if (component.confirmationNumber) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Confirmation Number' : 'Confirmation Number'}:</strong> ${component.confirmationNumber}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && (component.returnDepartureAirport || component.returnArrivalAirport)) {
                                    const returnAirlineText = component.returnAirline || component.returnFlightNumber
                                      ? ` (${[component.returnAirline || component.airline, component.returnFlightNumber].filter(Boolean).join(' ')})`
                                      : '';
                                    details += `<p><strong>Return Route:</strong> ${component.returnDepartureAirport || 'N/A'} → ${component.returnArrivalAirport || 'N/A'}${returnAirlineText}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnConfirmationNumber) {
                                    details += `<p><strong>Return Confirmation Number:</strong> ${component.returnConfirmationNumber}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnSeats) {
                                    details += `<p><strong>Return Seats:</strong> ${component.returnSeats}</p>`;
                                  }
                                  
                                  // Hotel details
                                  if (component.propertyName) {
                                    details += `<p><strong>Property:</strong> ${component.propertyName}</p>`;
                                  }
                                  if (component.location) {
                                    details += `<p><strong>Location:</strong> ${component.location}</p>`;
                                  }
                                  if (component.roomType) {
                                    details += `<p><strong>Room Type:</strong> ${component.roomType}</p>`;
                                  }
                                  
                                  // Restaurant details
                                  if (component.restaurantName) {
                                    details += `<p><strong>Restaurant:</strong> ${component.restaurantName}</p>`;
                                  }
                                  if (component.partySize) {
                                    details += `<p><strong>Party Size:</strong> ${component.partySize}</p>`;
                                  }
                                  
                                  // Activity details
                                  if (component.activityName) {
                                    details += `<p><strong>Activity:</strong> ${component.activityName}</p>`;
                                  }
                                  if (component.address) {
                                    details += `<p><strong>Address:</strong> ${component.address}</p>`;
                                  }
                                  
                                  return details;
                                };
                                
                                // Helper function to render attachments
                                const renderAttachments = (component) => {
                                  let attachmentHtml = '';
                                  
                                  if (component.photos && component.photos.length > 0) {
                                    attachmentHtml += '<div class="attachments-section"><h4>Photos:</h4>';
                                    component.photos.forEach(photo => {
                                      if (photo.url) {
                                        attachmentHtml += `<img src="${photo.url}" alt="${photo.name || 'Photo'}" style="max-width: 200px; max-height: 150px; margin: 5px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">`;
                                      }
                                    });
                                    attachmentHtml += '</div>';
                                  }
                                  
                                  if (component.documents && component.documents.length > 0) {
                                    attachmentHtml += '<div class="attachments-section"><h4>Documents:</h4><ul>';
                                    component.documents.forEach(doc => {
                                      if (doc.url && doc.name) {
                                        attachmentHtml += `<li><a href="${doc.url}" target="_blank" style="color: #2563eb; text-decoration: none;">${doc.name}</a></li>`;
                                      }
                                    });
                                    attachmentHtml += '</ul></div>';
                                  }
                                  
                                  return attachmentHtml;
                                };
                                
                                // Actual field values (departureDate, airline,
                                // etc.) live on comp.data, not comp itself -
                                // renderDates/renderDetails read comp.data.
                                return `
                                  <div class="component">
                                    <h3>${comp.title}</h3>
                                    ${comp.description ? `<p><strong>Description:</strong> ${comp.description}</p>` : ''}
                                    ${renderDates(comp.data || {})}
                                    ${renderDetails(comp.data || {})}
                                    ${renderAttachments(comp)}
                                    <div class="component-price">$${(parseFloat(comp.price) || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                  </div>
                                `;
                              }).join('')}
                          </div>
                          
                          <div class="pricing-summary">
                            <div class="pricing-row total">
                              <span class="pricing-label">Total Price:</span>
                              <span class="pricing-value">$${(quote.totalPrice || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            ${quote.depositAmount ? `
                              <div class="pricing-row">
                                <span class="pricing-label">Deposit Required:</span>
                                <span class="pricing-value">$${(quote.depositAmount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                            ` : ''}
                          </div>
                        </div>
                        
                        <div class="footer">
                          ${quote.agentInfo?.termsConditions ? `<p>${quote.agentInfo.termsConditions}</p>` : '<p>This quote is valid for 30 days from the date of issue.</p>'}
                        </div>
                      </div>
                    </body>
                    </html>
                  `;
                  previewWindow.document.write(previewContent);
                  previewWindow.document.close();
                }}
                disabled={!quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                Preview Quote
              </button>

              {isEditMode ? (
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleSaveQuote(false)}
                  disabled={loading}
                >
                  Save Changes
                </button>
              ) : (
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleSaveQuote(false)}
                  disabled={loading}
                >
                  Save Draft
                </button>
              )}

              <button 
                className="btn btn-primary"
                onClick={() => {
                  // For multi-option AI quotes, ask the agent whether to send
                  // the comparison link (all options) or just the active one.
                  if (quoteOptionGroup.length > 1) {
                    setShowSendModal(true);
                  } else {
                    handleSaveQuote(true, false, 'current');
                  }
                }}
                disabled={loading || !quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                {loading ? 'Sending...' : 'Send To Client'}
              </button>
            </div>
          </div>
        )}

        {/* Send Quote Step */}
        {activeStep === 'send' && (
          <div className="step-content">
            <h2>Send Quote</h2>
            <p>Quote sending functionality would go here...</p>
          </div>
        )}
      </div>

      {/* Send Quote Modal - choose Comparison vs Current Option for multi-option AI quotes */}
      <Modal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Quote to Client"
        maxWidth="480px"
      >
            <div className="send-quote-modal-body">
              <p>This quote has {quoteOptionGroup.length} package options. How would you like to send it?</p>

              <button
                type="button"
                className="send-quote-option-btn"
                onClick={() => {
                  setShowSendModal(false);
                  handleSaveQuote(true, false, 'comparison');
                }}
                disabled={loading}
              >
                <span className="send-quote-option-title">📊 Send Comparison (All Options)</span>
                <span className="send-quote-option-desc">
                  Client gets one link to a tabbed page comparing all {quoteOptionGroup.length} packages side by side.
                </span>
              </button>

              <button
                type="button"
                className="send-quote-option-btn"
                onClick={() => {
                  setShowSendModal(false);
                  handleSaveQuote(true, false, 'current');
                }}
                disabled={loading}
              >
                <span className="send-quote-option-title">📄 Send Current Option Only</span>
                <span className="send-quote-option-desc">
                  Client gets a link to just "{quoteOptionGroup[activeOptionIndex]?.optionName || 'this option'}" — no comparison.
                </span>
              </button>
            </div>
      </Modal>

      {/* Component Modal */}
      <Modal
        isOpen={showComponentModal}
        onClose={() => {
          setShowComponentModal(false);
          setSelectedComponentType('');
          setEditingComponent(null);
        }}
        title={`${editingComponent ? 'Edit' : 'Add'} ${componentTypes.find(t => t.id === selectedComponentType)?.name || ''}`}
        maxWidth="900px"
      >
        {renderComponentForm()}
      </Modal>

      {/* PDF Upload Modal */}
      {showPDFUploadModal && (
        <PDFQuoteUploadModal
          onClose={() => setShowPDFUploadModal(false)}
          onQuoteDataExtracted={(data) => {
            setPdfExtractedData(data);
            setShowPDFReviewModal(true);
          }}
        />
      )}

      {/* PDF Review Modal */}
      {showPDFReviewModal && pdfExtractedData && (
        <PDFQuoteReviewModal
          extractedData={pdfExtractedData}
          onConfirm={(components, metadata) => {
            // Track package group ID for related components
            const packageGroupId = Date.now().toString();
            let mainComponentTitle = '';

            // Add components to quote with relationship tracking
            components.forEach((comp, index) => {
              // Handle bundled pricing - set to 0 if included in package
              let componentPrice = 0;
              
              // For main component, use offer subtotal if available
              if (comp.isMainComponent && pdfExtractedData.offerSubtotal) {
                componentPrice = pdfExtractedData.offerSubtotal;
              } else if (comp.includedInPackage) {
                componentPrice = 0; // Will show as "Included in Package Price"
              } else if (comp.data.price !== null && comp.data.price !== undefined) {
                componentPrice = comp.data.price;
              }

              const componentData = {
                type: comp.type,
                title: comp.data.title || comp.typeName,
                price: componentPrice,
                ...comp.data,
                // Preserve AI extraction metadata
                aiExtracted: true,
                includedInPackage: comp.includedInPackage || false,
                packageGroupId: comp.linkedTo ? packageGroupId : null,
                isMainComponent: comp.isMainComponent || false,
                linkedTo: comp.linkedTo || null,
                relationshipType: comp.relationshipType || null,
                extractionReason: comp.reason || null
              };

              // Track main component title for description enhancement
              if (comp.isMainComponent) {
                mainComponentTitle = componentData.title;
              }

              // For linked components, enhance description with relationship info
              if (comp.linkedTo && comp.relationshipType) {
                const relationshipLabel = {
                  'insurance': '🛡️ Travel Protection',
                  'dining': '🍽️ Dining Plan',
                  'addon': '➕ Add-on Service',
                  'transportation': '🚗 Transportation',
                  'related': '🔗 Related Service'
                }[comp.relationshipType] || '🔗 Related Service';

                componentData.description = componentData.description 
                  ? `${relationshipLabel} - ${componentData.description}`
                  : relationshipLabel;
              }

              handleAddComponent(componentData);
            });

            // Add package extras as note to main component if present
            if (pdfExtractedData.packageExtras && pdfExtractedData.packageExtras.length > 0) {
              const extrasNote = '\n\n📦 Package Extras:\n' + 
                pdfExtractedData.packageExtras.map(extra => 
                  `• ${extra.name}${extra.included ? ' (Included)' : ` - $${extra.price?.toFixed(2) || '0.00'}`}${extra.description ? ': ' + extra.description : ''}`
                ).join('\n');

              // Update the main component's notes with extras info
              setQuote(prev => {
                const updatedComponents = prev.components.map(comp => {
                  if (comp.aiExtracted && comp.isMainComponent && comp.packageGroupId === packageGroupId) {
                    return {
                      ...comp,
                      data: {
                        ...comp.data,
                        special_requests: (comp.data.special_requests || '') + extrasNote
                      }
                    };
                  }
                  return comp;
                });
                return { ...prev, components: updatedComponents };
              });
            }

            // Update quote metadata if available
            if (metadata) {
              setQuote(prev => ({
                ...prev,
                quoteTitle: metadata.title || prev.quoteTitle,
                travelDates: {
                  start: metadata.travelStartDate || prev.travelDates.start,
                  end: metadata.travelEndDate || prev.travelDates.end
                },
                travelers: {
                  adults: metadata.adults || prev.travelers.adults,
                  children: metadata.children || prev.travelers.children
                },
                depositAmount: metadata.depositAmount || prev.depositAmount,
                depositDueDate: metadata.depositDueDate || prev.depositDueDate
              }));
            }

            setShowPDFReviewModal(false);
            setPdfExtractedData(null);
          }}
          onCancel={() => {
            setShowPDFReviewModal(false);
            setPdfExtractedData(null);
          }}
        />
      )}

      {/* Conversational Quote Builder */}
      {showConversationalBuilder && (
        <ConversationalQuoteBuilder
          onClose={() => setShowConversationalBuilder(false)}
          onQuoteCreated={(quoteData) => {
            // Handle quote creation from conversational builder
            setShowConversationalBuilder(false);
            // Future: populate quote with conversational data
          }}
          onFinalizeSuccess={handleAIQuoteFinalized}
        />
      )}

      {/* Component Library Modal */}
      <ComponentLibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Client Selection Modal */}
      <ClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        clients={clients}
        onSelectClient={handleClientSelect}
        onCreateClient={handleNewClient}
      />

      {/* Error display */}
      {errors.submit && (
        <div className="alert alert-error">
          {errors.submit}
        </div>
      )}
    </div>
  );
};

export default QuoteBuilder;
